import React from "react"

import { Grid } from "@mui/material"
import { Typography } from "@synapse-analytics/synapse-ui"
import moment from "moment"

/**
 * Interface for the tooltip that displays multiple time intervals
 * for a workflow's active periods
 */
interface MultipleIntervalsTooltipProps {
  active_intervals: Array<{
    /** End date of the interval */
    to_date: string
    /** Start date of the interval */
    from_date: string
  }>
}

/**
 * Component that renders a tooltip showing a list of time intervals
 * when a workflow was active
 *
 * @param props - Component props of type MultipleIntervalsTooltipProps
 * @returns React component displaying formatted date intervals
 */
export const MultipleIntervalsTooltip = (props: MultipleIntervalsTooltipProps): React.ReactElement => {
  const { active_intervals } = props

  return (
    <Grid item gap={1}>
      {active_intervals?.map((interval, index) => (
        <Grid item key={`${interval.from_date} -> ${interval.to_date}`}>
          <Typography variant={"p"} variantColor={2}>
            {`[${index + 1}]: `}
            {moment(interval.from_date).format("Do MMM YYYY")}
            {" -> "}
            {moment(interval.to_date).format("Do MMM YYYY")}
          </Typography>
        </Grid>
      ))}
    </Grid>
  )
}

/**
 * Interface representing the props for the DecisionsPerWorkflowItem component
 */
interface DecisionsPerWorkflowItemProps {
  /** Width percentage for the workflow item container */
  width: number
  /** Color for the chip indicator */
  chipColor: string
  /** Workflow data object */
  workflow: {
    /** Percentage of total decisions this workflow represents */
    percent: number
    /** Width value for layout calculations */
    width: number
    /** Name of the workflow */
    name: string
    /** Total count of decisions in this workflow */
    count: number
    /** Array of time intervals when the workflow was active */
    active_intervals: Array<{
      /** End date of the interval */
      to_date: string
      /** Start date of the interval */
      from_date: string
    }>
  }
}

/**
 * Component that displays detailed information about a single workflow's decisions
 * including count, percentage, name, and active time intervals.
 *
 * @param props - Component props of type DecisionsPerWorkflowItemProps
 * @returns React component displaying workflow decision information
 */
export function DecisionsPerWorkflowItem(props: Readonly<DecisionsPerWorkflowItemProps>): React.ReactElement {
  const { width, workflow, chipColor } = props

  return (
    <Grid item width={`${width}%`} key={workflow.name}>
      <Grid item xs={12} mb={2} gap={0.5} display={"flex"}>
        <Grid item>
          <Typography variant={"h2-bold"} noWrap>
            {workflow.count}{" "}
          </Typography>
        </Grid>
        <Grid item display={"flex"} alignItems={"center"}>
          <Typography variant="label-bold" noWrap variantColor={2}>
            ({workflow.percent}%)
          </Typography>
        </Grid>
      </Grid>

      <Grid container item xs={12} wrap="nowrap" display={"flex"}>
        <Grid item>
          <span
            key="chip"
            style={{
              display: "inline-flex",
              width: "10px",
              height: "10px",
              background: chipColor,
              borderRadius: "2px",
              marginRight: "4px",
            }}
          />
        </Grid>

        <Grid item xs width={"calc(100% - 20px)"}>
          <Typography variant={"a"} noWrap display="inline">
            {workflow.name}
          </Typography>
        </Grid>
      </Grid>

      {workflow?.active_intervals?.length === 0 && workflow.name !== "Others" && (
        <Grid item xs={12} pt={1}>
          <Typography variant={"p"} variantColor={2}>
            Intervals unavailable
          </Typography>
        </Grid>
      )}

      {workflow?.active_intervals?.length === 1 && (
        <Grid item xs={12} pt={1}>
          <Typography variant={"p"} variantColor={2}>
            {moment(workflow.active_intervals[0]?.from_date).format("Do MMM YYYY")}
            {" -> "}
          </Typography>
          <Typography variant={"p"} variantColor={2}>
            {moment(workflow.active_intervals[0]?.to_date).format("Do MMM YYYY")}
          </Typography>
        </Grid>
      )}

      {workflow?.active_intervals?.length > 1 && (
        <Grid item xs={12} pt={1}>
          <Typography
            variant={"p"}
            variantColor={2}
            tooltip={<MultipleIntervalsTooltip active_intervals={workflow.active_intervals} />}
          >
            Multiple intervals
          </Typography>
        </Grid>
      )}
    </Grid>
  )
}
